import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  BreadcrumbDivider,
  IconFormIcons,
  IconFormNames,
  Target,
} from 'shared/assets/icons';
import { CircleProgress } from 'shared/components/ui/circle-progress';
import { Icon } from 'shared/components/ui/icon';
import { Paths } from 'shared/routes/paths';
import { Goal } from 'shared/types/goal';
import { ID } from 'shared/types/id';
import { LifeArea } from 'shared/types/life-area';
import { getCompletionPercentage } from 'shared/utils/get-completion-percentage';
import { getGoalCompletion } from 'shared/utils/get-goal-completion';

import * as Styled from './goal-detail-breadcrumbs.style';

export type GoalDetailBreadcrumbsProps = {
  goal: Goal;
  goals: Goal[];
  lifeAreas: LifeArea[];
};

export const GoalDetailBreadcrumbs: React.FC<GoalDetailBreadcrumbsProps> = ({
  goal,
  goals,
  lifeAreas,
}) => {
  const breadcrumbs = useMemo(() => {
    type BreadcrumbItem = {
      id: ID;
      name: string;
      path: string;
      percentage: number;
      hasProgress: boolean;
      iconName?: IconFormNames | null;
      isGoal: boolean;
    };

    const goalsMap = new Map(goals.map((g) => [g.id, g]));

    const getAncestors = (curr: Goal, visited: Set<ID>): Goal[] => {
      const parentId = curr.parentIds?.[curr.parentIds.length - 1];
      if (!parentId) {
        return [];
      }

      const parent = goalsMap.get(parentId);

      if (!parent) {
        return [];
      }

      if (visited.has(parent.id)) {
        return [];
      }

      visited.add(parent.id);
      return [...getAncestors(parent, visited), parent];
    };

    const ancestors = getAncestors(goal, new Set([goal.id]));

    const items: BreadcrumbItem[] = ancestors.map((parent) => {
      const { start, current, end } = getGoalCompletion(parent, { goals });
      const percentage = getCompletionPercentage({ start, current, end });
      const hasProgress = end !== Infinity && !parent.completedAt;

      return {
        id: parent.id,
        name: parent.name,
        path: Paths.GoalDetail.replace(':id', parent.id),
        percentage,
        hasProgress,
        iconName: parent.iconName,
        isGoal: true,
      };
    });

    const rootGoal = ancestors[0] ?? goal;
    const lifeAreaId = rootGoal.lifeAreaId;
    const lifeArea = lifeAreas.find((la) => la.id === lifeAreaId);

    if (lifeArea) {
      items.unshift({
        id: lifeArea.id,
        name: lifeArea.name,
        path: Paths.LifeAreaDetail.replace(':id', lifeArea.id),
        percentage: 0,
        hasProgress: false,
        iconName: lifeArea.iconName,
        isGoal: false,
      });
    }

    return items;
  }, [goal, goals, lifeAreas]);

  if (!breadcrumbs.length) {
    return null;
  }

  return (
    <Styled.Container>
      {breadcrumbs.map((item, index) => (
        <React.Fragment key={item.id}>
          {index > 0 && (
            <Styled.Separator>
              <BreadcrumbDivider />
            </Styled.Separator>
          )}
          <Styled.BreadcrumbItem>
            <Link to={item.path}>
              <Styled.IconWrapper>
                {item.hasProgress ? (
                  <CircleProgress
                    percentage={item.percentage}
                    hidePercentage
                    isSmall
                  />
                ) : (
                  <Icon
                    icon={
                      item.iconName
                        ? IconFormIcons[item.iconName] ?? Target
                        : Target
                    }
                  />
                )}
              </Styled.IconWrapper>
              {item.name}
            </Link>
          </Styled.BreadcrumbItem>
        </React.Fragment>
      ))}
    </Styled.Container>
  );
};
